// src/pages/Gestion.js
import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useGlobalContext } from "../Config";
import { TabView, TabPanel } from "primereact/tabview";
import { DateTime } from "luxon";

const BANCOS_CHILE = [
  "Banco de Chile",
  "Banco Internacional",
  "BancoEstado",
  "Scotiabank Chile",
  "Banco de Crédito e Inversiones (BCI)",
  "Banco BICE",
  "HSBC Bank (Chile)",
  "Banco Santander-Chile",
  "Banco Itaú Chile",
  "Banco Falabella",
  "Banco Ripley",
  "Banco Consorcio",
  "Banco BTG Pactual Chile",
  "Tanner Banco Digital",
  "Tenpo Bank Chile",
  "J.P. Morgan Chase Bank, N.A.",
  "China Construction Bank, Agencia en Chile",
  "Bank of China, Agencia en Chile",
].map((nombre) => ({ label: nombre, value: nombre }));

const CODIGOS_PAIS = [
  { label: "🇨🇱 Chile (+56)", value: "+56" },
  { label: "🇦🇷 Argentina (+54)", value: "+54" },
  { label: "🇵🇪 Perú (+51)", value: "+51" },
  { label: "🇧🇴 Bolivia (+591)", value: "+591" },
  { label: "🇨🇴 Colombia (+57)", value: "+57" },
  { label: "🇧🇷 Brasil (+55)", value: "+55" },
  { label: "🇺🇾 Uruguay (+598)", value: "+598" },
  { label: "🇺🇸 EE.UU./Canadá (+1)", value: "+1" },
  { label: "🇪🇸 España (+34)", value: "+34" },
];

const HORAS_24 = Array.from({ length: 48 }, (_, i) => {
  const hh = String(Math.floor(i / 2)).padStart(2, "0");
  const mm = i % 2 === 0 ? "00" : "30";
  const value = `${hh}:${mm}`;
  return { label: value, value };
});

const formatearRut = (valor = "") => {
  let limpio = String(valor)
    .replace(/[^0-9kK]/g, "")
    .toUpperCase()
    .slice(0, 9);
  if (limpio.length <= 1) return limpio;
  const dv = limpio.slice(-1);
  let cuerpo = limpio.slice(0, -1);
  cuerpo = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${cuerpo}-${dv}`;
};

const separarTelefono = (telefono = "") => {
  const limpio = String(telefono).replace(/\s+/g, "");
  const codigo =
    [...CODIGOS_PAIS]
      .sort((a, b) => b.value.length - a.value.length)
      .find((p) => limpio.startsWith(p.value))?.value || "+56";
  return {
    codigo,
    numero: limpio.startsWith(codigo)
      ? limpio.slice(codigo.length)
      : limpio.replace(/^\+/, ""),
  };
};

const separarHorario = (horario = "") => {
  const [desde = "", hasta = ""] = String(horario)
    .split("-")
    .map((v) => v.trim());
  return { desde, hasta };
};

const Gestion = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { styleProperties, baseUrl, headerText } = useGlobalContext();
  const [medioPago, setMedioPago] = useState(null);
  const [antecedentesBancarios, setAntecedentesBancarios] = useState([]);
  const contactoVacio = {
    rut: "",
    nombre: "",
    mail: "",
    telefonoMovil: "",
    telefonoFijo: "",
    horarioDisponibilidad: "",
  };
  const [contactosCobranza, setContactosCobranza] = useState([]);
  const [guardandoContactos, setGuardandoContactos] = useState(false);
  const [mensajeContactos, setMensajeContactos] = useState("");
  const [contactosEnEdicion, setContactosEnEdicion] = useState({});
  const [contactosOriginales, setContactosOriginales] = useState({});
  const [esAbono, setEsAbono] = useState(false);
  const documento = location.state?.documento || null;
  const parseCLP = (val) => {
    if (val == null) return 0;
    if (typeof val === "number") return Math.trunc(val);
    const digits = String(val).replace(/[^\d-]/g, ""); // deja sólo dígitos y signo
    return digits ? Math.trunc(Number(digits)) : 0;
  };
  const deudaTotalNum = useMemo(() => {
    return parseCLP(documento?.deuda);
  }, [documento]);
  const [monto, setMonto] = useState(deudaTotalNum); // Monto editable
  const [errorMonto, setErrorMonto] = useState(false);
  const [fechaDeposito, setFechaDeposito] = useState(null);
  const fechaHoy = format(new Date(), "dd MMM. yy", { locale: es });

  useEffect(() => {
    const clienteId = documento?.cliente?.id;
    setContactosCobranza([]);
    setMensajeContactos("");
    setContactosEnEdicion({});
    setContactosOriginales({});
    if (!clienteId) return;

    let activo = true;
    fetch(`${baseUrl}clientes/contactos-cobranza?cliente=${clienteId}`, {
      headers: headerText,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => {
        if (activo)
          setContactosCobranza(
            Array.isArray(data.contactos) ? data.contactos : [],
          );
      })
      .catch(() => {
        if (activo) setContactosCobranza([]);
      });

    return () => {
      activo = false;
      setContactosCobranza([]);
      setMensajeContactos("");
      setContactosEnEdicion({});
      setContactosOriginales({});
    };
  }, [documento?.cliente?.id, baseUrl]);

  useEffect(() => {
    const clienteId = documento?.cliente?.id;
    if (!clienteId) return;
    fetch(`${baseUrl}pagos/antecedentes-bancarios?cliente=${clienteId}`, {
      headers: headerText,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) =>
        setAntecedentesBancarios(
          Array.isArray(data.antecedentes) ? data.antecedentes : [],
        ),
      )
      .catch(() => setAntecedentesBancarios([]));
  }, [documento?.cliente?.id, baseUrl]);

  const opcionesAntecedentes = (tipo) => {
    const vistos = new Set();
    const opciones = [{ label: "Ingresar nuevos datos", value: "NUEVO" }];
    antecedentesBancarios
      .filter((a) => a.tipo === tipo)
      .forEach((a, i) => {
        const clave = `${a.banco || ""}|${a.cuenta || ""}|${
          a.plaza || ""
        }`.toLowerCase();
        if (vistos.has(clave)) return;
        vistos.add(clave);
        opciones.push({
          label: `${a.banco || "Sin banco"}${
            a.cuenta ? ` · Cta. ${a.cuenta}` : ""
          }${a.plaza ? ` · ${a.plaza}` : ""} (${a.medioPago})`,
          value: `${tipo}-${i}`,
          antecedente: a,
        });
      });
    return opciones;
  };

  const aplicarAntecedente = (
    opciones,
    valor,
    setBanco,
    setCuenta,
    setPlaza,
  ) => {
    if (valor === "NUEVO") {
      setBanco("");
      setCuenta("");
      setPlaza("");
      return;
    }
    const seleccionado = opciones.find((o) => o.value === valor)?.antecedente;
    if (!seleccionado) return;
    setBanco(seleccionado.banco || "");
    setCuenta(seleccionado.cuenta || "");
    setPlaza(seleccionado.plaza || "");
  };

  const actualizarContacto = (index, campo, valor) => {
    setContactosCobranza((actuales) =>
      actuales.map((c, i) => (i === index ? { ...c, [campo]: valor } : c)),
    );
  };

  const actualizarTelefono = (index, campo, codigo, numero) => {
    const soloNumero = String(numero || "")
      .replace(/\D/g, "")
      .slice(0, 15);
    actualizarContacto(
      index,
      campo,
      soloNumero ? `${codigo}${soloNumero}` : "",
    );
  };

  const actualizarHorario = (index, parte, valor) => {
    const actual = separarHorario(
      contactosCobranza[index]?.horarioDisponibilidad,
    );
    const desde = parte === "desde" ? valor : actual.desde;
    const hasta = parte === "hasta" ? valor : actual.hasta;
    actualizarContacto(
      index,
      "horarioDisponibilidad",
      desde || hasta ? `${desde} - ${hasta}` : "",
    );
  };

  const persistirContactos = async (contactos) => {
    const res = await fetch(
      `${baseUrl}clientes/contactos-cobranza?cliente=${documento?.cliente?.id}`,
      {
        method: "PUT",
        headers: { ...headerText, "Content-Type": "application/json" },
        body: JSON.stringify({ contactos }),
      },
    );
    if (!res.ok) throw new Error("No fue posible guardar los contactos");
  };

  const recargarContactos = async () => {
    const res = await fetch(
      `${baseUrl}clientes/contactos-cobranza?cliente=${documento?.cliente?.id}`,
      { headers: headerText },
    );
    if (!res.ok) throw new Error("No fue posible recargar los contactos");
    const data = await res.json();
    setContactosCobranza(Array.isArray(data.contactos) ? data.contactos : []);
  };

  const agregarContacto = () => {
    const index = contactosCobranza.length;
    setContactosCobranza((actuales) => [
      ...actuales,
      { ...contactoVacio, _nuevo: true },
    ]);
    setContactosEnEdicion((actual) => ({ ...actual, [index]: true }));
    setMensajeContactos("");
  };

  const editarContacto = (index) => {
    setContactosOriginales((actual) => ({
      ...actual,
      [index]: { ...contactosCobranza[index] },
    }));
    setContactosEnEdicion((actual) => ({ ...actual, [index]: true }));
    setMensajeContactos("");
  };

  const cancelarEdicionContacto = (index) => {
    const contacto = contactosCobranza[index];
    if (contacto?._nuevo) {
      setContactosCobranza((actuales) =>
        actuales.filter((_, i) => i !== index),
      );
      setContactosEnEdicion({});
      setContactosOriginales({});
      return;
    }
    const original = contactosOriginales[index];
    if (original)
      setContactosCobranza((actuales) =>
        actuales.map((c, i) => (i === index ? original : c)),
      );
    setContactosEnEdicion((actual) => ({ ...actual, [index]: false }));
    setContactosOriginales((actual) => {
      const copia = { ...actual };
      delete copia[index];
      return copia;
    });
    setMensajeContactos("");
  };

  const guardarContacto = async () => {
    setGuardandoContactos(true);
    setMensajeContactos("");
    try {
      const correoInvalido = contactosCobranza.find(
        (c) => c.mail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.mail),
      );
      if (correoInvalido)
        throw new Error("Ingresa un correo electrónico válido");
      const horarioInvalido = contactosCobranza.find((c) => {
        if (!c.horarioDisponibilidad) return false;
        const { desde, hasta } = separarHorario(c.horarioDisponibilidad);
        return !desde || !hasta || desde >= hasta;
      });
      if (horarioInvalido)
        throw new Error("El horario debe tener una hora Desde y Hasta válida");
      const limpios = contactosCobranza.map(({ _nuevo, ...c }) => c);
      await persistirContactos(limpios);
      await recargarContactos();
      setContactosEnEdicion({});
      setContactosOriginales({});
      setMensajeContactos("Contactos actualizados");
    } catch (e) {
      setMensajeContactos(e.message || "No fue posible guardar los contactos");
    } finally {
      setGuardandoContactos(false);
    }
  };

  const guardarContactos = async () => {
    setGuardandoContactos(true);
    setMensajeContactos("");
    try {
      await persistirContactos(contactosCobranza);
      setMensajeContactos("Contactos actualizados");
    } catch (e) {
      setMensajeContactos(e.message);
    } finally {
      setGuardandoContactos(false);
    }
  };

  const quitarContacto = async (index) => {
    const contacto = contactosCobranza[index];
    const restantes = contactosCobranza.filter((_, idx) => idx !== index);

    // Si todavía no fue guardado, basta con quitar el borrador del estado local.
    if (!contacto?.id && !contacto?.idPersonaContactos) {
      setContactosCobranza(restantes);
      setMensajeContactos("");
      return;
    }

    setGuardandoContactos(true);
    setMensajeContactos("");
    try {
      await persistirContactos(restantes);
      setContactosCobranza(restantes);
      setMensajeContactos("Contactos actualizados");
    } catch (e) {
      setMensajeContactos("No fue posible quitar el contacto");
    } finally {
      setGuardandoContactos(false);
    }
  };

  ////////////// Variables PARA TRANSFERENCIAS //////////////

  // ---- Transferencias: estados controlados ----
  const [antecedenteOrigenTransf, setAntecedenteOrigenTransf] = useState(null);
  const [antecedenteDestinoTransf, setAntecedenteDestinoTransf] =
    useState(null);
  const [bancoOrigen, setBancoOrigen] = useState("");
  const [cuentaOrigen, setCuentaOrigen] = useState("");
  const [plazaOrigen, setPlazaOrigen] = useState("");
  const [fechaTransferencia, setFechaTransferencia] = useState(null);
  const [esAbonoTransf, setEsAbonoTransf] = useState(false);
  const [montoTransf, setMontoTransf] = useState(documento?.deuda || 0);
  const [errorMontoTransf, setErrorMontoTransf] = useState(false);
  const [bancoDestino, setBancoDestino] = useState("");
  const [cuentaDestino, setCuentaDestino] = useState("");
  const [plazaDestino, setPlazaDestino] = useState("");
  const [obsTransf, setObsTransf] = useState("");
  const [savingTransf, setSavingTransf] = useState(false);
  const [msgTransf, setMsgTransf] = useState(null);
  const [errTransf, setErrTransf] = useState(null);

  const handleGuardarTransferencia = async () => {
    setMsgTransf(null);
    setErrTransf(null);

    // Validación de monto según abono
    const monto = esAbonoTransf
      ? Math.min(Number(montoTransf || 0), Number(deudaTotalNum))
      : Number(deudaTotalNum);

    if (esAbonoTransf) {
      if (monto <= 0) {
        setErrTransf("El monto debe ser mayor que 0.");
        return;
      }
      if (monto > deudaTotalNum) {
        setErrTransf("El monto no puede superar la deuda total.");
        return;
      }
    }

    const body = {
      abono: Boolean(esAbonoTransf),
      bancoDestino: bancoDestino || "",
      bancoOrigen: bancoOrigen || "",
      cliente: Number(documento?.cliente?.id || 0), // integer
      cuentaCorrienteDestino: cuentaDestino || "",
      cuentaCorrienteOrigen: cuentaOrigen || "",
      folio: String(documento?.folio ?? ""), // string
      monto: Math.trunc(Number.isFinite(monto) ? monto : 0), // integer
      observaciones: obsTransf || "",
      plazaDestino: plazaDestino || "",
      plazaOrigen: plazaOrigen || "",
    };

    try {
      setSavingTransf(true);

      const res = await fetch(`${baseUrl}pagos/transferencia`, {
        method: "POST",
        headers: {
          ...headerText,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al guardar transferencia"}`,
        );
      }

      setMsgTransf("Transferencia registrada correctamente.");
      navigate("/pagos", {
        state: {
          folio: documento?.folio ?? "",
          documento,
        },
      });
    } catch (e) {
      setErrTransf(e.message || "No se pudo registrar la transferencia.");
    } finally {
      setSavingTransf(false);
    }
  };

  ////////////// Variables PARA CHEQUES //////////////

  // ---- Cheques: estados controlados ----
  const [esAbonoCheque, setEsAbonoCheque] = useState(false);
  const [montoCheque, setMontoCheque] = useState(deudaTotalNum);
  const [errorMontoCheque, setErrorMontoCheque] = useState(false);

  const [antecedenteOrigenChk, setAntecedenteOrigenChk] = useState(null);
  const [antecedenteDestinoChk, setAntecedenteDestinoChk] = useState(null);
  const [bancoOrigenChk, setBancoOrigenChk] = useState("");
  const [cuentaOrigenChk, setCuentaOrigenChk] = useState("");
  const [plazaOrigenChk, setPlazaOrigenChk] = useState("");
  const [fechaDocumento, setFechaDocumento] = useState(null);
  const [bancoDestinoChk, setBancoDestinoChk] = useState("");
  const [cuentaDestinoChk, setCuentaDestinoChk] = useState("");
  const [plazaDestinoChk, setPlazaDestinoChk] = useState("");
  const [fechaRetiro, setFechaRetiro] = useState(null);
  const [numeroSerie, setNumeroSerie] = useState("");
  const [nombreContacto, setNombreContacto] = useState("");
  const [telefonoContacto, setTelefonoContacto] = useState("");
  const [celularContacto, setCelularContacto] = useState("");
  const [correoContacto, setCorreoContacto] = useState("");
  const [lugarRetiro, setLugarRetiro] = useState("");
  const [horaRetiro, setHoraRetiro] = useState("");
  const [obsCheque, setObsCheque] = useState("");

  const [savingCheque, setSavingCheque] = useState(false);
  const [msgCheque, setMsgCheque] = useState(null);
  const [errCheque, setErrCheque] = useState(null);

  const handleGuardarCheque = async () => {
    setMsgCheque(null);
    setErrCheque(null);

    const monto = esAbonoCheque
      ? Math.min(Number(montoCheque || 0), Number(deudaTotalNum))
      : Number(deudaTotalNum);

    if (esAbonoCheque) {
      if (monto <= 0) {
        setErrCheque("El monto debe ser mayor que 0.");
        return;
      }
      if (monto > deudaTotalNum) {
        setErrCheque("El monto no puede superar la deuda total.");
        return;
      }
    }

    const body = {
      abono: Boolean(esAbonoCheque),
      bancoDestino: bancoDestinoChk || "",
      bancoOrigen: bancoOrigenChk || "",
      celularContacto: celularContacto || "",
      cliente: Number(documento?.cliente?.id || 0),
      correoContacto: correoContacto || "",
      cuentaCorrienteDestino: cuentaDestinoChk || "",
      cuentaCorrienteOrigen: cuentaOrigenChk || "",
      fecha: toIsoOrNull(fechaDeposito), // ejemplo ISO del backend
      fechaDocumento: toIsoOrNull(fechaDocumento),
      fechaRetiro: toIsoOrNull(fechaRetiro),
      folio: String(documento?.folio ?? ""),
      horaRetiro: horaRetiro || "",
      lugarRetiro: lugarRetiro || "",
      monto: Math.trunc(Number.isFinite(monto) ? monto : 0),
      nombreContacto: nombreContacto || "",
      numeroSerie: numeroSerie || "",
      observaciones: obsCheque || "",
      plazaDestino: plazaDestinoChk || "",
      plazaOrigen: plazaOrigenChk || "",
      telefonoContacto: telefonoContacto || "",
    };

    try {
      setSavingCheque(true);
      const res = await fetch(`${baseUrl}pagos/cheque`, {
        method: "POST",
        headers: { ...headerText, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al registrar cheque"}`,
        );
      }
      setMsgCheque("Cheque registrado correctamente.");
      navigate("/pagos", {
        state: {
          folio: documento?.folio ?? "",
          documento,
        },
      });
    } catch (e) {
      setErrCheque(e.message || "No se pudo registrar el cheque.");
    } finally {
      setSavingCheque(false);
    }
  };
  ////////////// Variables PARA DEPÓSITOS //////////////

  // ---- Depósitos: estados controlados ----
  const [esAbonoDep, setEsAbonoDep] = useState(false);
  const [montoDep, setMontoDep] = useState(deudaTotalNum);
  const [errorMontoDep, setErrorMontoDep] = useState(false);
  const [antecedenteDestinoDep, setAntecedenteDestinoDep] = useState(null);
  const [bancoDestinoDep, setBancoDestinoDep] = useState("");
  const [cuentaDestinoDep, setCuentaDestinoDep] = useState("");
  const [plazaDestinoDep, setPlazaDestinoDep] = useState("");
  const [numeroVale, setNumeroVale] = useState("");
  const [obsDep, setObsDep] = useState("");
  const [savingDep, setSavingDep] = useState(false);
  const [msgDep, setMsgDep] = useState(null);
  const [errDep, setErrDep] = useState(null);

  const handleGuardarDeposito = async () => {
    setMsgDep(null);
    setErrDep(null);

    // Validación monto según abono
    const monto = esAbonoDep
      ? Math.min(Number(montoDep || 0), Number(deudaTotalNum))
      : Number(deudaTotalNum);

    if (esAbonoDep) {
      if (monto <= 0) {
        setErrDep("El monto debe ser mayor que 0.");
        return;
      }
      if (monto > deudaTotalNum) {
        setErrDep("El monto no puede superar la deuda total.");
        return;
      }
    }

    const body = {
      abono: Boolean(esAbonoDep),
      bancoDestino: bancoDestinoDep || "",
      cliente: Number(documento?.cliente?.id || 0),
      cuentaCorrienteDestino: cuentaDestinoDep || "",
      folio: String(documento?.folio ?? ""),
      monto: Math.trunc(Number.isFinite(monto) ? monto : 0),
      numeroVale: numeroVale || "",
      observaciones: obsDep || "",
      plazaDestino: plazaDestinoDep || "",
    };

    try {
      setSavingDep(true);
      const res = await fetch(`${baseUrl}pagos/deposito`, {
        method: "POST",
        headers: { ...headerText, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al registrar depósito"}`,
        );
      }
      setMsgDep("Depósito registrado correctamente.");
      navigate("/pagos", {
        state: {
          folio: documento?.folio ?? "",
          documento,
        },
      });
    } catch (e) {
      setErrDep(e.message || "No se pudo registrar el depósito.");
    } finally {
      setSavingDep(false);
    }
  };

  ////////////// Variables PARA WEBPAY //////////////

  // ---- Webpay: estados controlados ----
  const [esAbonoWebpay, setEsAbonoWebpay] = useState(false);
  const [montoWebpay, setMontoWebpay] = useState(deudaTotalNum);
  const [errorMontoWebpay, setErrorMontoWebpay] = useState(false);

  const [tarjetaWp, setTarjetaWp] = useState(""); // VISA/MASTER/...
  const [tipoWp, setTipoWp] = useState(""); // CREDITO/DEBITO/...
  const [origenWp, setOrigenWp] = useState(""); // NACIONAL/INTERNACIONAL
  const [cuotasWp, setCuotasWp] = useState(""); // string
  const [titularWp, setTitularWp] = useState(""); // nombre titular
  const [numeroAutWp, setNumeroAutWp] = useState(""); // código autorización
  const [obsWebpay, setObsWebpay] = useState("");

  const [savingWebpay, setSavingWebpay] = useState(false);
  const [msgWebpay, setMsgWebpay] = useState(null);
  const [errWebpay, setErrWebpay] = useState(null);

  const handleGuardarWebpay = async () => {
    setMsgWebpay(null);
    setErrWebpay(null);

    // Validación de monto según Abono (misma regla)
    const monto = esAbonoWebpay
      ? Math.min(Number(montoWebpay || 0), Number(deudaTotalNum))
      : Number(deudaTotalNum);

    if (esAbonoWebpay) {
      if (monto <= 0) {
        setErrWebpay("El monto debe ser mayor que 0.");
        return;
      }
      if (monto > deudaTotalNum) {
        setErrWebpay("El monto no puede superar la deuda total.");
        return;
      }
    }

    const body = {
      abono: Boolean(esAbonoWebpay),
      cliente: Number(documento?.cliente?.id || 0),
      cuotas: String(cuotasWp || ""), // string
      folio: String(documento?.folio ?? ""),
      monto: Math.trunc(Number.isFinite(monto) ? monto : 0), // integer
      numeroAutorizacion: numeroAutWp || "",
      observaciones: obsWebpay || "",
      origen: origenWp || "", // "NACIONAL" | "INTERNACIONAL"
      tarjeta: tarjetaWp || "", // "VISA" | "MASTER CARD" | ...
      tipo: tipoWp || "", // "CREDITO" | "DEBITO" | ...
      titular: titularWp || "",
    };

    try {
      setSavingWebpay(true);
      const res = await fetch(`${baseUrl}pagos/webpay`, {
        method: "POST",
        headers: { ...headerText, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al registrar Webpay"}`,
        );
      }
      setMsgWebpay("Pago Webpay registrado correctamente.");
      navigate("/pagos", {
        state: {
          folio: documento?.folio ?? "",
          documento,
        },
      });
    } catch (e) {
      setErrWebpay(e.message || "No se pudo registrar el pago Webpay.");
    } finally {
      setSavingWebpay(false);
    }
  };

  // --- Compromiso ---
  const [compromisoFecha, setCompromisoFecha] = useState(null); // Calendar
  const [savingCompromiso, setSavingCompromiso] = useState(false);
  const [msgCompromiso, setMsgCompromiso] = useState(null);
  const [errCompromiso, setErrCompromiso] = useState(null);

  // --- Observación ---
  const [textoObs, setTextoObs] = useState("");
  const [savingObs, setSavingObs] = useState(false);
  const [msgObs, setMsgObs] = useState(null);
  const [errObs, setErrObs] = useState(null);
  const tipoPagoIdByCode = {
    transferencia: 1,
    cheque: 2,
    deposito: 3,
    vale_vista: 4,
    webpay: 5,
  };

  const resetCompromisoForm = () => {
    setCompromisoFecha(null);
    setMedioPago(null);
    setEsAbono(false);
    setMonto(deudaTotalNum);
    setErrorMonto(false);
    setMsgCompromiso(null);
    setErrCompromiso(null);
  };

  // --- Compromisos previos (consulta por folio) ---
  const [compromisosPrevios, setCompromisosPrevios] = useState([]);
  const [loadingCompromisos, setLoadingCompromisos] = useState(false);
  const [errCompromisos, setErrCompromisos] = useState(null);
  const yesNoPill = (v) => (
    <span
      style={{
        display: "inline-block",
        padding: "0.15rem 0.5rem",
        borderRadius: "9999px",
        background: v ? "#E6F6EA" : "#F8EAEA",
        color: v ? "#0b7e20" : "#b91c1c",
        fontSize: "0.75rem",
        fontWeight: 700,
      }}
    >
      {v ? "Sí" : "No"}
    </span>
  );

  const loadCompromisos = async () => {
    setErrCompromisos(null);
    if (!documento?.folio) {
      setCompromisosPrevios([]);
      return;
    }
    try {
      setLoadingCompromisos(true);

      const params = new URLSearchParams();
      params.append("folios", String(documento.folio)); // si tienes varios, haz forEach
      params.append("_ts", String(Date.now())); // anti-caché

      const headersGet = Object.fromEntries(
        Object.entries(headerText || {}).filter(
          ([k]) => k?.toLowerCase() !== "content-type", // no pongas Content-Type en GET
        ),
      );

      const res = await fetch(
        `${baseUrl}compromisos/por-folios?${params.toString()}`,
        {
          method: "GET",
          headers: { ...headersGet, Accept: "application/json" },
          credentials: "include",
        },
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al obtener compromisos"}`,
        );
      }

      const data = await res.json();
      const list = Array.isArray(data?.compromisos) ? data.compromisos : [];
      list.sort(
        (a, b) =>
          (parseBackendDate(b?.fecha)?.toMillis?.() ?? 0) -
          (parseBackendDate(a?.fecha)?.toMillis?.() ?? 0),
      );
      setCompromisosPrevios(list);
    } catch (e) {
      setErrCompromisos(e.message || "No se pudieron cargar los compromisos.");
      setCompromisosPrevios([]);
    } finally {
      setLoadingCompromisos(false);
    }
  };

  // cargar al montar / cuando cambie el folio
  useEffect(() => {
    loadCompromisos();
  }, []);

  const buildDocumentosForPayload = () =>
    rows
      .filter(Boolean)
      .map((row) => ({
        cliente: { id: Number(row?.cliente?.id || 0) },
        tipoDocumento: { id: Number(row?.tipoDocumento?.id || 0) },
        folio: String(row?.folio ?? ""),
      }))
      // filtra documentos incompletos
      .filter((d) => d.cliente.id > 0 && d.tipoDocumento.id > 0 && d.folio);

  const handleGuardarCompromiso = async () => {
    setMsgCompromiso(null);
    setErrCompromiso(null);

    // Validar monto según "Abono"
    const montoNum = esAbono
      ? Math.min(Number(monto || 0), Number(deudaTotalNum))
      : Number(deudaTotalNum);

    if (esAbono) {
      if (montoNum <= 0)
        return setErrCompromiso("El monto debe ser mayor que 0.");
      if (montoNum > deudaTotalNum)
        return setErrCompromiso("El monto no puede superar la deuda total.");
    }

    // Resolver id del tipo de documento de pago
    const tipoPagoId =
      typeof medioPago === "number"
        ? medioPago
        : tipoPagoIdByCode[medioPago] || null;
    if (!tipoPagoId)
      return setErrCompromiso("Seleccione un medio de pago válido.");

    // Documentos para el detalle_compromiso
    const documentosPayload = buildDocumentosForPayload();
    if (!documentosPayload.length) {
      return setErrCompromiso(
        "No hay documentos válidos para asociar al compromiso.",
      );
    }

    // Body SOLO con los campos del Compromiso (sin monto/abono)
    const body = {
      tipoDocumentoPago: { id: Number(tipoPagoId) },
      fechaPago: toIsoOrNull(compromisoFecha),
      documentos: documentosPayload,
    };

    // Query params para el controller (abono & monto)
    const params = new URLSearchParams({
      monto: String(Math.trunc(montoNum)),
      abono: String(Boolean(esAbono)),
    });

    try {
      setSavingCompromiso(true);
      const res = await fetch(
        `${baseUrl}compromiso/agregar?${params.toString()}`,
        {
          method: "POST",
          headers: { ...headerText, "Content-Type": "application/json" },
          body: JSON.stringify({
            tipoDocumentoPago: { id: Number(tipoPagoId) },
            fechaPago: toIsoOrNull(compromisoFecha),
            documentos: documentosPayload,
          }),
          credentials: "include",
        },
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al crear compromiso"}`,
        );
      }

      setMsgCompromiso("Compromiso guardado correctamente.");
      await loadCompromisos();
      resetCompromisoForm();
    } catch (e) {
      setErrCompromiso(e.message || "No se pudo guardar el compromiso.");
    } finally {
      setSavingCompromiso(false);
    }
  };

  const handleGuardarObservacion = async () => {
    setMsgObs(null);
    setErrObs(null);

    if (!textoObs?.trim()) {
      return setErrObs("Ingrese una observación.");
    }

    const body = {
      documento: {
        cliente: { id: Number(documento?.cliente?.id || 0) },
        tipoDocumento: { id: Number(documento?.tipoDocumento?.id || 0) },
        folio: String(documento?.folio ?? ""),
      },
      texto: textoObs.trim(),
    };

    try {
      setSavingObs(true);
      const res = await fetch(`${baseUrl}observaciones`, {
        method: "POST",
        headers: { ...headerText, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `Error ${res.status}: ${text || "al crear observación"}`,
        );
      }

      setMsgObs("Observación guardada correctamente.");
      setTextoObs("");
    } catch (e) {
      setErrObs(e.message || "No se pudo guardar la observación.");
    } finally {
      setSavingObs(false);
    }
  };

  const toIsoOrNull = (d) =>
    d instanceof Date && !isNaN(d) ? d.toISOString() : null;

  // rows: si viene lista -> úsala; si viene 1 doc -> arma array con ese doc
  const rows = useMemo(() => {
    if (
      Array.isArray(documento?.documentos) &&
      documento.documentos.length > 0
    ) {
      return documento.documentos;
    }
    return documento ? [documento] : [];
  }, [documento]);

  const mediosPago = [
    { label: "TRANSFERENCIA", value: "transferencia" },
    { label: "CHEQUE", value: "cheque" },
    { label: "DEPÓSITO", value: "deposito" },
    { label: "VALE VISTA", value: "vale_vista" },
    { label: "WEBPAY", value: "webpay" },
  ];

  const tarjetas = [
    { label: "VISA", value: "VISA" },
    { label: "MASTER CARD", value: "MASTER CARD" },
    { label: "AMERICAN EXPRESS", value: "AMERICAN EXPRESS" },
    { label: "MAGNA", value: "MAGNA" },
  ];

  const tiposTarjeta = [
    { label: "CRÉDITO", value: "CREDITO" },
    { label: "DÉBITO", value: "DEBITO" },
    { label: "PREPAGO", value: "PREPAGO" },
  ];

  const origenes = [
    { label: "NACIONAL", value: "NACIONAL" },
    { label: "INTERNACIONAL", value: "INTERNACIONAL" },
  ];

  const formatCLP = (val) => {
    const n = parseCLP(val);
    return n.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const formatCurrency = (value) =>
    value?.toLocaleString("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    });

  const parseBackendDate = (val) => {
    if (!val) return null;

    // Si viene desde Calendar como Date
    if (val instanceof Date) {
      return DateTime.fromJSDate(val, { zone: "America/Santiago" });
    }

    if (typeof val !== "string") return null;

    const trimmed = val.trim();
    // El backend manda "CLT"/"CLST" (no IANA) -> quitamos
    const cleaned = trimmed.replace(/\sCLST|\sCLT/g, "");

    // 1) Intento principal: "Tue May 30 00:00:00 2023" (día con o sin 0)
    let dt = DateTime.fromFormat(cleaned, "ccc LLL dd HH:mm:ss yyyy", {
      zone: "America/Santiago",
      locale: "en", // MUY importante: meses en inglés
    });

    if (!dt.isValid) {
      dt = DateTime.fromFormat(cleaned, "ccc LLL d HH:mm:ss yyyy", {
        zone: "America/Santiago",
        locale: "en",
      });
    }

    // 2) Fallback: ISO
    if (!dt.isValid) {
      dt = DateTime.fromISO(trimmed, { zone: "America/Santiago" });
    }

    // 3) Fallback final: Date nativo
    if (!dt.isValid) {
      const js = new Date(trimmed);
      if (!isNaN(js)) {
        dt = DateTime.fromJSDate(js, { zone: "America/Santiago" });
      }
    }

    return dt.isValid ? dt : null;
  };

  // Formateador para las celdas del DataTable
  const formatDateCell = (val) => {
    const dt = parseBackendDate(val);
    return dt ? dt.toFormat("dd/MM/yyyy") : "—";
  };

  useEffect(() => {
    if (!esAbono) {
      setMonto(deudaTotalNum);
    }
  }, [esAbono, deudaTotalNum]);

  useEffect(() => {
    if (!esAbonoTransf) {
      setMontoTransf(deudaTotalNum);
      setErrorMontoTransf(false);
    }
  }, [esAbonoTransf, deudaTotalNum]);

  return (
    <div style={{ padding: "1rem", background: "#f6f7f9", minHeight: "100vh" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            outlined
            onClick={() => navigate("/documentos")}
            style={{
              borderRadius: "2rem",
              padding: "0.5rem 1rem",
              fontWeight: 600,
              color: "#0051a2",
              borderColor: "#0051a2",
            }}
          />
          <h2
            style={{
              margin: 0,
              fontWeight: 700,
              fontSize: "1.75rem",
              color: styleProperties?.primaryText || "#2c2c2c",
            }}
          >
            Compromisos
          </h2>
        </div>

        {/* Fecha Card */}
        <div
          style={{
            minWidth: 180,
            padding: "0.75rem 1rem",
            borderRadius: 16,
            background: "#ffffff",
            border: "1px solid #e0dddc",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "1px",
              color: "#8a8d91",
            }}
          >
            FECHA
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <i
              className="pi pi-calendar"
              style={{ fontSize: "1.5rem", color: "#2b94f0" }}
            />
            <span
              style={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "#1e1e1e",
              }}
            >
              {fechaHoy}
            </span>
          </div>
        </div>
      </div>

      {/* Cliente Card */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #e0dddc",
          borderRadius: 16,
          padding: "1rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
          marginBottom: "2rem",
        }}
      >
        {/* Imagen o avatar */}
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 12,
            background: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "1rem",
            flexShrink: 0,
          }}
        >
          <i
            className="pi pi-image"
            style={{ fontSize: "1.5rem", color: "#b0b0b0" }}
          />
        </div>

        {/* Info del cliente */}
        <div style={{ flexGrow: 1 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: "1.1rem",
              color: "#1e1e1e",
              marginBottom: "0.25rem",
            }}
          >
            {documento?.cliente?.razonSocial || "—"}
          </div>
          <div style={{ color: "#6b7280", fontSize: "0.95rem" }}>
            {documento?.cliente?.rut || "—"}
          </div>
        </div>

        {/* Contactos de cobranza del cliente */}
        <div
          style={{
            flex: "1 1 560px",
            margin: "0 1rem",
            borderLeft: "1px solid #e5e7eb",
            paddingLeft: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <div>
              <strong>Contactos</strong>
              <div
                style={{ color: "#6b7280", fontSize: "0.78rem", marginTop: 2 }}
              >
                Hasta 2 contactos de cobranza
              </div>
            </div>
            {contactosCobranza.length < 2 && (
              <Button
                label="Agregar"
                icon="pi pi-plus"
                text
                size="small"
                onClick={agregarContacto}
                disabled={guardandoContactos}
              />
            )}
          </div>

          {contactosCobranza.length === 0 && (
            <div
              style={{
                padding: "0.8rem",
                border: "1px dashed #d1d5db",
                borderRadius: 10,
                color: "#6b7280",
                fontSize: "0.88rem",
              }}
            >
              Sin contactos registrados.
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(285px, 1fr))",
              gap: 10,
            }}
          >
            {contactosCobranza.map((c, i) => {
              const editando =
                Boolean(contactosEnEdicion[i]) || Boolean(c._nuevo);
              return (
                <div
                  key={c.id || `nuevo-${i}`}
                  style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: "0.75rem",
                  }}
                >
                  {!editando ? (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              color: "#1f2937",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {c.nombre || "Sin nombre"}
                          </div>
                          <div
                            style={{ color: "#6b7280", fontSize: "0.82rem" }}
                          >
                            {c.rut || "Sin RUT"}
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 2,
                          }}
                        >
                          <Button
                            icon="pi pi-pencil"
                            text
                            rounded
                            size="small"
                            tooltip="Editar"
                            onClick={() => editarContacto(i)}
                            disabled={guardandoContactos}
                          />
                          <Button
                            icon="pi pi-trash"
                            text
                            rounded
                            severity="danger"
                            size="small"
                            tooltip="Quitar"
                            onClick={() => quitarContacto(i)}
                            disabled={guardandoContactos}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gap: 5,
                          fontSize: "0.86rem",
                          color: "#374151",
                        }}
                      >
                        <div>
                          <i
                            className="pi pi-envelope"
                            style={{ width: 20, color: "#6b7280" }}
                          />
                          {c.mail || "Sin correo"}
                        </div>
                        <div>
                          <i
                            className="pi pi-mobile"
                            style={{ width: 20, color: "#6b7280" }}
                          />
                          {c.telefonoMovil || "Sin teléfono móvil"}
                        </div>
                        {c.telefonoFijo && (
                          <div>
                            <i
                              className="pi pi-phone"
                              style={{ width: 20, color: "#6b7280" }}
                            />
                            {c.telefonoFijo}
                          </div>
                        )}
                        <div>
                          <i
                            className="pi pi-clock"
                            style={{ width: 20, color: "#6b7280" }}
                          />
                          {c.horarioDisponibilidad || "Sin horario definido"}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>
                        {c._nuevo ? "Nuevo contacto" : "Editar contacto"}
                      </div>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 6,
                        }}
                      >
                        <InputText
                          value={c.rut || ""}
                          onChange={(e) =>
                            actualizarContacto(
                              i,
                              "rut",
                              formatearRut(e.target.value),
                            )
                          }
                          placeholder="12.345.678-9"
                          maxLength={12}
                          disabled={Boolean(c.idPersonaContactos)}
                        />
                        <InputText
                          value={c.nombre || ""}
                          onChange={(e) =>
                            actualizarContacto(i, "nombre", e.target.value)
                          }
                          placeholder="Nombre"
                          disabled={Boolean(c.idPersonaContactos)}
                        />
                        <InputText
                          type="email"
                          value={c.mail || ""}
                          onChange={(e) =>
                            actualizarContacto(i, "mail", e.target.value.trim())
                          }
                          placeholder="correo@dominio.cl"
                          disabled={Boolean(c.idPersonaContactos)}
                          style={{ gridColumn: "1 / -1" }}
                        />
                        {[
                          { campo: "telefonoMovil", label: "Teléfono móvil" },
                          { campo: "telefonoFijo", label: "Teléfono fijo" },
                        ].map(({ campo, label }) => {
                          const tel = separarTelefono(c[campo]);
                          return (
                            <div
                              key={campo}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "145px 1fr",
                                gap: 6,
                              }}
                            >
                              <Dropdown
                                value={tel.codigo}
                                options={CODIGOS_PAIS}
                                onChange={(e) =>
                                  actualizarTelefono(
                                    i,
                                    campo,
                                    e.value,
                                    tel.numero,
                                  )
                                }
                                disabled={Boolean(c.idPersonaContactos)}
                              />
                              <InputText
                                inputMode="numeric"
                                value={tel.numero}
                                onChange={(e) =>
                                  actualizarTelefono(
                                    i,
                                    campo,
                                    tel.codigo,
                                    e.target.value,
                                  )
                                }
                                placeholder={label}
                                disabled={Boolean(c.idPersonaContactos)}
                              />
                            </div>
                          );
                        })}
                        {(() => {
                          const horario = separarHorario(
                            c.horarioDisponibilidad,
                          );
                          return (
                            <div
                              style={{
                                gridColumn: "1 / -1",
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 6,
                              }}
                            >
                              <span className="p-float-label">
                                <Dropdown
                                  inputId={`hora-desde-${i}`}
                                  value={horario.desde}
                                  options={HORAS_24}
                                  onChange={(e) =>
                                    actualizarHorario(i, "desde", e.value)
                                  }
                                  placeholder="Desde"
                                  style={{ width: "100%" }}
                                />
                                <label htmlFor={`hora-desde-${i}`}>Desde</label>
                              </span>
                              <span className="p-float-label">
                                <Dropdown
                                  inputId={`hora-hasta-${i}`}
                                  value={horario.hasta}
                                  options={HORAS_24}
                                  onChange={(e) =>
                                    actualizarHorario(i, "hasta", e.value)
                                  }
                                  placeholder="Hasta"
                                  style={{ width: "100%" }}
                                />
                                <label htmlFor={`hora-hasta-${i}`}>Hasta</label>
                              </span>
                            </div>
                          );
                        })()}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 6,
                          marginTop: 8,
                        }}
                      >
                        <Button
                          label="Cancelar"
                          icon="pi pi-times"
                          text
                          size="small"
                          onClick={() => cancelarEdicionContacto(i)}
                          disabled={guardandoContactos}
                        />
                        <Button
                          label="Guardar"
                          icon="pi pi-check"
                          size="small"
                          onClick={guardarContacto}
                          loading={guardandoContactos}
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          {mensajeContactos && (
            <small
              style={{
                display: "block",
                marginTop: 6,
                color: mensajeContactos.includes("actualizados")
                  ? "#0b7e20"
                  : "#e24c4c",
              }}
            >
              {mensajeContactos}
            </small>
          )}
        </div>

        {/* Total Deuda Card */}
        <div
          style={{
            background: "#f9fafb",
            borderRadius: 12,
            padding: "0.75rem 1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            minWidth: 160,
            justifyContent: "center",
          }}
        >
          <i
            className="pi pi-money-bill"
            style={{ fontSize: "1.5rem", color: "#2b94f0" }}
          />
          <div>
            <div
              style={{ fontWeight: 600, fontSize: "0.85rem", color: "#333" }}
            >
              Total Deuda
            </div>
            <div
              style={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e1e1e" }}
            >
              {formatCurrency(deudaTotalNum)}
            </div>
          </div>
        </div>

        <Button
          label="Historial pagos"
          icon="pi pi-history"
          outlined
          style={{
            borderRadius: "2rem",
            padding: "0.5rem 1rem",
            fontWeight: 600,
            color: "#0051a2",
            borderColor: "#0051a2",
            marginLeft: "10px",
          }}
          onClick={() =>
            navigate("/pagos", {
              state: {
                folio: documento?.folio ?? "",
                documento, // opcional: así reusamos el mismo datatable de documentos
              },
            })
          }
        />
      </div>

      {/* Tabla de documentos */}
      <div style={{ background: "#fff", borderRadius: 12, padding: "1rem" }}>
        <DataTable
          className="p-datatable-gridlines"
          value={rows}
          stripedRows
          emptyMessage="No hay documentos disponibles."
        >
          <Column
            field="tipoDocumento.descripcion"
            header="Tipo Documento"
            style={{ minWidth: "150px" }}
          />
          <Column field="folio" header="Folio" />
          <Column
            field="fechaEmision"
            header="Fecha de Emisión"
            body={(row) => formatDateCell(row.fechaEmision)}
          />
          <Column
            field="fechaVencimiento"
            header="Fecha de Vencimiento"
            body={(row) => formatDateCell(row.fechaVencimiento)}
          />
          <Column field="dias" header="Días" />
          <Column
            field="neto"
            header="Neto"
            body={(row) => formatCurrency(row.neto)}
          />
          <Column
            field="iva"
            header="IVA"
            body={(row) => formatCurrency(row.iva)}
          />
          <Column
            field="total"
            header="Total"
            body={(row) => formatCurrency(row.total)}
          />
          <Column
            field="deuda"
            header="Deuda"
            body={(row) => formatCurrency(row.deuda)}
          />
        </DataTable>
      </div>
      {/* TabView de medios de pago */}
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: "1rem",
          marginTop: "2rem",
        }}
      >
        <TabView>
          <TabPanel
            header={
              <span>
                <i
                  className="pi pi-file-edit"
                  style={{ marginRight: "0.5rem", color: "#2b94f0" }}
                />
                Compromiso
              </span>
            }
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
            >
              {/* Observaciones */}
              {/*<div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Observaciones
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    alignItems: "flex-start",
                  }}
                >
                  <textarea
                    rows={4}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      border: "1px solid #ccc",
                      padding: "0.75rem",
                      fontSize: "1rem",
                      resize: "vertical",
                    }}
                    placeholder="Agregar observación"
                    value={textoObs}
                    onChange={(e) => setTextoObs(e.target.value)}
                  />
                  <Button
                    label={savingObs ? "Guardando..." : "Guardar"}
                    icon="pi pi-save"
                    outlined
                    style={{
                      whiteSpace: "nowrap",
                      borderRadius: "20px",
                      padding: "0.6rem 1.2rem",
                      color: "#2b94f0",
                      borderColor: "#2b94f0",
                      fontWeight: 600,
                    }}
                    onClick={handleGuardarObservacion}
                    disabled={savingObs}
                  />
                </div>
              </div>*/}

              {/* Establecer compromiso */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Establecer Compromiso de Pago
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                    alignItems: "center",
                  }}
                >
                  {/* Fecha */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: "0.25rem",
                        display: "block",
                      }}
                    >
                      Fecha de Compromiso
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      style={{ width: "100%" }}
                      showIcon
                      value={compromisoFecha}
                      onChange={(e) => setCompromisoFecha(e.value)}
                    />
                  </div>

                  {/* Medio de pago */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: "0.25rem",
                        display: "block",
                      }}
                    >
                      Medio de Pago
                    </label>
                    <Dropdown
                      placeholder="Seleccione"
                      options={mediosPago}
                      optionLabel="label"
                      optionValue="value"
                      style={{ width: "100%" }}
                      value={medioPago}
                      onChange={(e) => setMedioPago(e.value)}
                    />
                  </div>

                  {/* Monto */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: "0.25rem",
                        display: "block",
                      }}
                    >
                      Monto
                    </label>
                    <InputText
                      value={formatCLP(monto)}
                      onChange={(e) => {
                        const numeric = parseCLP(e.target.value);
                        setMonto(numeric);
                        setErrorMonto(numeric > deudaTotalNum);
                      }}
                      disabled={!esAbono}
                      style={{
                        width: "100%",
                        borderColor: errorMonto ? "#e24c4c" : undefined,
                        backgroundColor: errorMonto ? "#fff4f4" : undefined,
                      }}
                      key={esAbono ? "editable" : "locked"}
                    />
                    {errorMonto && (
                      <small
                        style={{
                          color: "#e24c4c",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        El monto no puede superar la deuda total (
                        {formatCLP(deudaTotalNum)}).
                      </small>
                    )}
                  </div>

                  {/* Abono */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Abono
                    </label>
                    <InputSwitch
                      checked={esAbono}
                      onChange={(e) => setEsAbono(e.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: "1.5rem" }}>
                  <Button
                    label={savingCompromiso ? "Guardando..." : "Guardar"}
                    icon="pi pi-save"
                    outlined
                    style={{
                      borderRadius: 20,
                      padding: "0.6rem 1.2rem",
                      color: "#2b94f0",
                      borderColor: "#2b94f0",
                      fontWeight: 600,
                    }}
                    disabled={errorMonto || savingCompromiso}
                    onClick={handleGuardarCompromiso}
                  />
                </div>
              </div>

              {/* Compromisos anteriores */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Compromisos anteriores
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "0.75rem" }} />

                {loadingCompromisos && (
                  <p style={{ color: "#888", fontStyle: "italic" }}>
                    Cargando compromisos…
                  </p>
                )}
                {errCompromisos && (
                  <p style={{ color: "#e24c4c" }}>{errCompromisos}</p>
                )}

                {!loadingCompromisos &&
                  !errCompromisos &&
                  compromisosPrevios.length === 0 && (
                    <p style={{ color: "#888", fontStyle: "italic" }}>
                      no existen compromisos de pago anteriores
                    </p>
                  )}

                {!loadingCompromisos &&
                  !errCompromisos &&
                  compromisosPrevios.length > 0 && (
                    <div style={{ display: "grid", gap: "0.75rem" }}>
                      {compromisosPrevios.map((c) => (
                        <div
                          key={c?.id}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: "0.75rem 1rem",
                            background: "#fff",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              gap: "1.25rem",
                              flexWrap: "wrap",
                              alignItems: "center",
                              marginBottom: "0.5rem",
                            }}
                          >
                            <div style={{ minWidth: 180 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#6b7280",
                                  fontWeight: 600,
                                }}
                              >
                                Fecha compromiso
                              </div>
                              <div
                                style={{ fontWeight: 700, color: "#111827" }}
                              >
                                {formatDateCell(c?.fecha)}
                              </div>
                            </div>

                            <div style={{ minWidth: 180 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#6b7280",
                                  fontWeight: 600,
                                }}
                              >
                                Fecha de pago
                              </div>
                              <div
                                style={{ fontWeight: 700, color: "#111827" }}
                              >
                                {formatDateCell(c?.fechaPago)}
                              </div>
                            </div>

                            <div style={{ minWidth: 200 }}>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#6b7280",
                                  fontWeight: 600,
                                }}
                              >
                                Medio de pago
                              </div>
                              <div
                                style={{ fontWeight: 700, color: "#111827" }}
                              >
                                {c?.tipoDocumentoPago?.descripcion || "—"}
                              </div>
                            </div>

                            {/*<div style={{ minWidth: 120 }}>
                              *<div
                                style={{
                                  fontSize: 12,
                                  color: "#6b7280",
                                  fontWeight: 600,
                                }}
                              >
                                Abono total
                              </div>
                              <div>{yesNoPill(Boolean(c?.abono))}</div>
                            </div>*/}
                          </div>

                          {/* Detalles */}
                          <div>
                            <div
                              style={{
                                fontSize: 12,
                                color: "#6b7280",
                                fontWeight: 600,
                                marginBottom: 6,
                              }}
                            >
                              Detalles
                            </div>
                            {(Array.isArray(c?.detalles) ? c.detalles : [])
                              .length === 0 ? (
                              <div
                                style={{
                                  color: "#6b7280",
                                  fontStyle: "italic",
                                }}
                              >
                                Sin detalles
                              </div>
                            ) : (
                              <div style={{ display: "grid", gap: 6 }}>
                                {c.detalles.map((d, idx) => (
                                  <div
                                    key={`${c?.id}-${idx}`}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr 1fr",
                                      gap: "0.5rem",
                                      padding: "0.5rem 0.75rem",
                                      background: "#f9fafb",
                                      borderRadius: 8,
                                    }}
                                  >
                                    <div>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: "#6b7280",
                                        }}
                                      >
                                        Monto
                                      </div>
                                      <div style={{ fontWeight: 600 }}>
                                        {formatCLP(d?.monto)}
                                      </div>
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: "#6b7280",
                                        }}
                                      >
                                        Abono
                                      </div>
                                      <div>{yesNoPill(Boolean(d?.abono))}</div>
                                    </div>
                                    <div>
                                      <div
                                        style={{
                                          fontSize: 12,
                                          color: "#6b7280",
                                        }}
                                      >
                                        Folio
                                      </div>
                                      <div style={{ fontWeight: 600 }}>
                                        {d?.documentoFolio || "—"}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          </TabPanel>

          <TabPanel
            header={
              <span>
                <i
                  className="pi pi-money-bill"
                  style={{ marginRight: "0.5rem", color: "#2b94f0" }}
                />
                Transferencias
              </span>
            }
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
            >
              {/* Información general */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información general
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                    alignItems: "center",
                  }}
                >
                  {/* Fecha */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Fecha de transferencia
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      showIcon
                      value={fechaTransferencia}
                      onChange={(e) => setFechaTransferencia(e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>

                  {/* Monto */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Monto
                    </label>
                    <InputText
                      value={formatCLP(montoTransf)}
                      onChange={(e) => {
                        const numeric = parseCLP(e.target.value);
                        setMontoTransf(numeric);
                        setErrorMontoTransf(numeric > deudaTotalNum);
                      }}
                      disabled={!esAbonoTransf}
                      style={{
                        width: "100%",
                        borderColor: errorMontoTransf ? "#e24c4c" : undefined,
                        backgroundColor: errorMontoTransf
                          ? "#fff4f4"
                          : undefined,
                      }}
                      key={esAbonoTransf ? "editable" : "locked"}
                    />
                    {errorMontoTransf && (
                      <small
                        style={{
                          color: "#e24c4c",
                          marginTop: 4,
                          display: "block",
                        }}
                      >
                        El monto no puede superar la deuda total (
                        {formatCLP(deudaTotalNum)}).
                      </small>
                    )}
                  </div>

                  {/* Abono */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                      }}
                    >
                      Abono
                    </label>
                    <InputSwitch
                      checked={esAbonoTransf}
                      onChange={(e) => setEsAbonoTransf(e.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Información de Origen */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información de Origen
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ width: "100%", gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Datos bancarios usados anteriormente
                    </label>
                    <Dropdown
                      value={antecedenteOrigenTransf}
                      options={opcionesAntecedentes("ORIGEN")}
                      onChange={(e) => {
                        setAntecedenteOrigenTransf(e.value);
                        aplicarAntecedente(
                          opcionesAntecedentes("ORIGEN"),
                          e.value,
                          setBancoOrigen,
                          setCuentaOrigen,
                          setPlazaOrigen,
                        );
                      }}
                      placeholder="Seleccione datos anteriores o ingrese nuevos"
                      filter
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Banco
                    </label>
                    <Dropdown
                      value={bancoOrigen}
                      options={BANCOS_CHILE}
                      onChange={(e) => setBancoOrigen(e.value)}
                      placeholder="Seleccione banco"
                      filter
                      showClear
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Número de cuenta
                    </label>
                    <InputText
                      placeholder="Ej: 354 856 852 46"
                      style={{ width: "100%" }}
                      value={cuentaOrigen}
                      type="number"
                      onChange={(e) => setCuentaOrigen(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Plaza
                    </label>
                    <InputText
                      placeholder="Ej: Santiago"
                      style={{ width: "100%" }}
                      value={plazaOrigen}
                      onChange={(e) => setPlazaOrigen(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Información de Destino */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información de Destino
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ width: "100%", gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Datos bancarios usados anteriormente
                    </label>
                    <Dropdown
                      value={antecedenteDestinoTransf}
                      options={opcionesAntecedentes("DESTINO")}
                      onChange={(e) => {
                        setAntecedenteDestinoTransf(e.value);
                        aplicarAntecedente(
                          opcionesAntecedentes("DESTINO"),
                          e.value,
                          setBancoDestino,
                          setCuentaDestino,
                          setPlazaDestino,
                        );
                      }}
                      placeholder="Seleccione datos anteriores o ingrese nuevos"
                      filter
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Banco
                    </label>
                    <Dropdown
                      value={bancoDestino}
                      options={BANCOS_CHILE}
                      onChange={(e) => setBancoDestino(e.value)}
                      placeholder="Seleccione banco"
                      filter
                      showClear
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Número de cuenta
                    </label>
                    <InputText
                      placeholder="Ej: 354 856 852 46"
                      style={{ width: "100%" }}
                      value={cuentaDestino}
                      type="number"
                      onChange={(e) => setCuentaDestino(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Plaza
                    </label>
                    <InputText
                      placeholder="Ej: Santiago"
                      style={{ width: "100%" }}
                      value={plazaDestino}
                      onChange={(e) => setPlazaDestino(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Observaciones
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <InputText
                  placeholder="Escriba aquí"
                  style={{ width: "100%" }}
                  value={obsTransf}
                  onChange={(e) => setObsTransf(e.target.value)}
                />
              </div>

              {/* Guardar */}
              <div>
                <Button
                  label={savingTransf ? "Guardando..." : "Guardar"}
                  icon="pi pi-save"
                  outlined
                  style={{
                    borderRadius: "20px",
                    padding: "0.6rem 1.2rem",
                    color: "#2b94f0",
                    borderColor: "#2b94f0",
                    fontWeight: 600,
                  }}
                  disabled={errorMontoTransf || savingTransf}
                  onClick={handleGuardarTransferencia}
                />

                {/* Feedback simple */}
                {msgTransf && (
                  <small style={{ color: "#0b7e20", marginLeft: 12 }}>
                    {msgTransf}
                  </small>
                )}
                {errTransf && (
                  <small style={{ color: "#e24c4c", marginLeft: 12 }}>
                    {errTransf}
                  </small>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel
            header={
              <span>
                <i
                  className="pi pi-book"
                  style={{ marginRight: "0.5rem", color: "#2b94f0" }}
                />
                Cheques
              </span>
            }
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
            >
              {/* Información general */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información general
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                    alignItems: "center",
                  }}
                >
                  {/* Fecha */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Fecha de depósito
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      showIcon
                      value={fechaDeposito}
                      onChange={(e) => setFechaDeposito(e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>

                  {/* Monto */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Monto
                    </label>
                    <InputText
                      value={formatCLP(montoCheque)}
                      onChange={(e) => {
                        const numeric = parseCLP(e.target.value);
                        setMontoCheque(numeric);
                        setErrorMontoCheque(
                          esAbonoCheque && numeric > deudaTotalNum,
                        );
                      }}
                      disabled={!esAbonoCheque}
                      style={{
                        width: "100%",
                        borderColor: errorMontoCheque ? "#e24c4c" : undefined,
                        backgroundColor: errorMontoCheque
                          ? "#fff4f4"
                          : undefined,
                      }}
                      key={esAbonoCheque ? "editable-cheque" : "locked-cheque"}
                    />
                    {errorMontoCheque && (
                      <small
                        style={{
                          color: "#e24c4c",
                          marginTop: 4,
                          display: "block",
                        }}
                      >
                        El monto no puede superar la deuda total (
                        {formatCLP(deudaTotalNum)}).
                      </small>
                    )}
                  </div>

                  {/* Abono */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                      }}
                    >
                      Abono
                    </label>
                    <InputSwitch
                      checked={esAbonoCheque}
                      onChange={(e) => {
                        const checked = e.value;
                        setEsAbonoCheque(checked);
                        if (!checked) {
                          setMontoCheque(deudaTotalNum);
                          setErrorMontoCheque(false);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Información de Destino */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información del Cheque
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ width: "100%", gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Datos bancarios usados anteriormente
                    </label>
                    <Dropdown
                      value={antecedenteOrigenChk}
                      options={opcionesAntecedentes("ORIGEN")}
                      onChange={(e) => {
                        setAntecedenteOrigenChk(e.value);
                        aplicarAntecedente(
                          opcionesAntecedentes("ORIGEN"),
                          e.value,
                          setBancoOrigenChk,
                          setCuentaOrigenChk,
                          setPlazaOrigenChk,
                        );
                      }}
                      placeholder="Seleccione datos anteriores o ingrese nuevos"
                      filter
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Banco
                    </label>
                    <Dropdown
                      value={bancoOrigenChk}
                      options={BANCOS_CHILE}
                      onChange={(e) => setBancoOrigenChk(e.value)}
                      placeholder="Seleccione banco"
                      filter
                      showClear
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Número de cuenta
                    </label>
                    <InputText
                      placeholder="Ej: 354 856 852 46"
                      style={{ width: "100%" }}
                      value={cuentaOrigenChk}
                      type="number"
                      onChange={(e) => setCuentaOrigenChk(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Plaza
                    </label>
                    <InputText
                      placeholder="Ej: Santiago"
                      style={{ width: "100%" }}
                      value={plazaOrigenChk}
                      onChange={(e) => setPlazaOrigenChk(e.target.value)}
                    />
                  </div>
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Numero de Serie
                    </label>
                    <InputText
                      placeholder="Ej: 123456"
                      style={{ width: "100%" }}
                      value={numeroSerie}
                      type="number"
                      onChange={(e) => setNumeroSerie(e.target.value)}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Fecha de Documento
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      showIcon
                      value={fechaDocumento}
                      onChange={(e) => setFechaDocumento(e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>

              {/* Información de Destino */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información de Destino
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ width: "100%", gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Datos bancarios usados anteriormente
                    </label>
                    <Dropdown
                      value={antecedenteDestinoChk}
                      options={opcionesAntecedentes("DESTINO")}
                      onChange={(e) => {
                        setAntecedenteDestinoChk(e.value);
                        aplicarAntecedente(
                          opcionesAntecedentes("DESTINO"),
                          e.value,
                          setBancoDestinoChk,
                          setCuentaDestinoChk,
                          setPlazaDestinoChk,
                        );
                      }}
                      placeholder="Seleccione datos anteriores o ingrese nuevos"
                      filter
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Banco
                    </label>
                    <Dropdown
                      value={bancoDestinoChk}
                      options={BANCOS_CHILE}
                      onChange={(e) => setBancoDestinoChk(e.value)}
                      placeholder="Seleccione banco"
                      filter
                      showClear
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Número de cuenta
                    </label>
                    <InputText
                      placeholder="Ej: 354 856 852 46"
                      style={{ width: "100%" }}
                      value={cuentaDestinoChk}
                      type="number"
                      onChange={(e) => setCuentaDestinoChk(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Plaza
                    </label>
                    <InputText
                      placeholder="Ej: Santiago"
                      style={{ width: "100%" }}
                      value={plazaDestinoChk}
                      onChange={(e) => setPlazaDestinoChk(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información del Retiro
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Contacto
                    </label>
                    <InputText
                      placeholder="Nombre del contacto"
                      style={{ width: "100%" }}
                      value={nombreContacto}
                      onChange={(e) => setNombreContacto(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Teléfono
                    </label>
                    <InputText
                      placeholder="Ej: 2 1234 5678"
                      style={{ width: "100%" }}
                      value={telefonoContacto}
                      onChange={(e) => setTelefonoContacto(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Celular
                    </label>
                    <InputText
                      placeholder="Ej: 9 1234 5678"
                      style={{ width: "100%" }}
                      value={celularContacto}
                      onChange={(e) => setCelularContacto(e.target.value)}
                    />
                  </div>
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Correo Electrónico
                    </label>
                    <InputText
                      placeholder="Ej: correo@correo.com"
                      style={{ width: "100%" }}
                      value={correoContacto}
                      onChange={(e) => setCorreoContacto(e.target.value)}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Fecha de retiro
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      showIcon
                      value={fechaRetiro}
                      onChange={(e) => setFechaRetiro(e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Hora
                    </label>
                    <InputText
                      placeholder="Ej: 13:00"
                      style={{ width: "100%" }}
                      value={horaRetiro}
                      onChange={(e) => setHoraRetiro(e.target.value)}
                    />
                  </div>
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Lugar
                    </label>
                    <InputText
                      placeholder="Ej: Alameda"
                      style={{ width: "100%" }}
                      value={lugarRetiro}
                      onChange={(e) => setLugarRetiro(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Observaciones
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <InputText
                  placeholder="Escriba aquí"
                  style={{ width: "100%" }}
                  value={obsCheque}
                  onChange={(e) => setObsCheque(e.target.value)}
                />
              </div>

              {/* Guardar */}
              <div>
                <Button
                  label={savingCheque ? "Guardando..." : "Guardar"}
                  icon="pi pi-save"
                  outlined
                  style={{
                    borderRadius: "20px",
                    padding: "0.6rem 1.2rem",
                    color: "#2b94f0",
                    borderColor: "#2b94f0",
                    fontWeight: 600,
                  }}
                  disabled={errorMontoCheque || savingCheque}
                  onClick={handleGuardarCheque}
                />

                {msgCheque && (
                  <small
                    style={{ color: "#0b7e20", marginTop: 8, display: "block" }}
                  >
                    {msgCheque}
                  </small>
                )}
                {errCheque && (
                  <small
                    style={{ color: "#e24c4c", marginTop: 8, display: "block" }}
                  >
                    {errCheque}
                  </small>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel
            header={
              <span>
                <i
                  className="pi pi-money-bill"
                  style={{ marginRight: "0.5rem", color: "#2b94f0" }}
                />
                Depósitos
              </span>
            }
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
            >
              {/* Información general */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información general
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                    alignItems: "center",
                  }}
                >
                  {/* Fecha */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Fecha de depósito
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      showIcon
                      value={fechaDeposito}
                      onChange={(e) => setFechaDeposito(e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Numero de Vale
                    </label>
                    <InputText
                      placeholder="Ej: 123456"
                      style={{ width: "100%" }}
                      value={numeroVale}
                      type="number"
                      onChange={(e) => setNumeroVale(e.target.value)}
                    />
                  </div>

                  {/* Monto */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Monto
                    </label>
                    <InputText
                      value={formatCLP(montoDep)}
                      onChange={(e) => {
                        const numeric = parseCLP(e.target.value);
                        setMontoDep(numeric);
                        setErrorMontoDep(esAbonoDep && numeric > deudaTotalNum);
                      }}
                      disabled={!esAbonoDep}
                      style={{
                        width: "100%",
                        borderColor: errorMontoDep ? "#e24c4c" : undefined,
                        backgroundColor: errorMontoDep ? "#fff4f4" : undefined,
                      }}
                      key={esAbonoDep ? "editable-dep" : "locked-dep"}
                    />
                    {errorMontoDep && (
                      <small
                        style={{
                          color: "#e24c4c",
                          marginTop: 4,
                          display: "block",
                        }}
                      >
                        El monto no puede superar la deuda total (
                        {formatCLP(deudaTotalNum)}).
                      </small>
                    )}
                  </div>

                  {/* Abono */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                      }}
                    >
                      Abono
                    </label>
                    <InputSwitch
                      checked={esAbonoDep}
                      onChange={(e) => {
                        const checked = e.value;
                        setEsAbonoDep(checked);
                        if (!checked) {
                          setMontoDep(deudaTotalNum);
                          setErrorMontoDep(false);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Información de Destino */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información de Destino
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  <div style={{ width: "100%", gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Datos bancarios usados anteriormente
                    </label>
                    <Dropdown
                      value={antecedenteDestinoDep}
                      options={opcionesAntecedentes("DESTINO")}
                      onChange={(e) => {
                        setAntecedenteDestinoDep(e.value);
                        aplicarAntecedente(
                          opcionesAntecedentes("DESTINO"),
                          e.value,
                          setBancoDestinoDep,
                          setCuentaDestinoDep,
                          setPlazaDestinoDep,
                        );
                      }}
                      placeholder="Seleccione datos anteriores o ingrese nuevos"
                      filter
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Banco
                    </label>
                    <Dropdown
                      value={bancoDestinoDep}
                      options={BANCOS_CHILE}
                      onChange={(e) => setBancoDestinoDep(e.value)}
                      placeholder="Seleccione banco"
                      filter
                      showClear
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Número de cuenta
                    </label>

                    <InputText
                      placeholder="Ej: 354 856 852 46"
                      style={{ width: "100%" }}
                      value={cuentaDestinoDep}
                      type="number"
                      onChange={(e) => setCuentaDestinoDep(e.target.value)}
                    />
                  </div>

                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Plaza
                    </label>
                    <InputText
                      placeholder="Ej: Santiago"
                      style={{ width: "100%" }}
                      value={plazaDestinoDep}
                      onChange={(e) => setPlazaDestinoDep(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Observaciones
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <InputText
                  placeholder="Escriba aquí"
                  style={{ width: "100%" }}
                  value={obsDep}
                  onChange={(e) => setObsDep(e.target.value)}
                />
              </div>

              {/* Guardar */}
              <div>
                <Button
                  label={savingDep ? "Guardando..." : "Guardar"}
                  icon="pi pi-save"
                  outlined
                  style={{
                    borderRadius: "20px",
                    padding: "0.6rem 1.2rem",
                    color: "#2b94f0",
                    borderColor: "#2b94f0",
                    fontWeight: 600,
                  }}
                  disabled={errorMontoDep || savingDep}
                  onClick={handleGuardarDeposito}
                />

                {msgDep && (
                  <small
                    style={{ color: "#0b7e20", marginTop: 8, display: "block" }}
                  >
                    {msgDep}
                  </small>
                )}
                {errDep && (
                  <small
                    style={{ color: "#e24c4c", marginTop: 8, display: "block" }}
                  >
                    {errDep}
                  </small>
                )}
              </div>
            </div>
          </TabPanel>

          <TabPanel
            header={
              <span>
                <i
                  className="pi pi-money-bill"
                  style={{ marginRight: "0.5rem", color: "#2b94f0" }}
                />
                Webpay
              </span>
            }
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
            >
              {/* Información general */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información general
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                    alignItems: "center",
                  }}
                >
                  {/* Fecha */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Fecha de depósito
                    </label>
                    <Calendar
                      dateFormat="dd/mm/yy"
                      placeholder="dd/mm/aaaa"
                      showIcon
                      value={fechaDeposito}
                      onChange={(e) => setFechaDeposito(e.value)}
                      style={{ width: "100%" }}
                    />
                  </div>

                  {/* Monto */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Monto
                    </label>
                    <InputText
                      value={formatCLP(montoWebpay)}
                      onChange={(e) => {
                        const numeric = parseCLP(e.target.value);
                        setMontoWebpay(numeric);
                        setErrorMontoWebpay(
                          esAbonoWebpay && numeric > deudaTotalNum,
                        );
                      }}
                      disabled={!esAbonoWebpay}
                      style={{
                        width: "100%",
                        borderColor: errorMontoWebpay ? "#e24c4c" : undefined,
                        backgroundColor: errorMontoWebpay
                          ? "#fff4f4"
                          : undefined,
                      }}
                      key={esAbonoWebpay ? "editable-wp" : "locked-wp"}
                    />
                    {errorMontoWebpay && (
                      <small
                        style={{
                          color: "#e24c4c",
                          marginTop: 4,
                          display: "block",
                        }}
                      >
                        El monto no puede superar la deuda total (
                        {formatCLP(deudaTotalNum)}).
                      </small>
                    )}
                  </div>

                  {/* Abono */}
                  <div>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                      }}
                    >
                      Abono
                    </label>
                    <InputSwitch
                      checked={esAbonoWebpay}
                      onChange={(e) => {
                        const checked = e.value;
                        setEsAbonoWebpay(checked);
                        if (!checked) {
                          setMontoWebpay(deudaTotalNum);
                          setErrorMontoWebpay(false);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Información de la transferencia */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Información de la Transferencia
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "1.5rem",
                  }}
                >
                  {/* Tarjeta */}
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Tarjeta
                    </label>
                    <Dropdown
                      options={tarjetas}
                      placeholder="Seleccione"
                      style={{ width: "100%" }}
                      value={tarjetaWp}
                      onChange={(e) => setTarjetaWp(e.value)}
                    />
                  </div>

                  {/* Tipo */}
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Tipo
                    </label>
                    <Dropdown
                      options={tiposTarjeta}
                      placeholder="Seleccione"
                      style={{ width: "100%" }}
                      value={tipoWp}
                      onChange={(e) => setTipoWp(e.value)}
                    />
                  </div>

                  {/* Origen */}
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Origen
                    </label>
                    <Dropdown
                      options={origenes}
                      placeholder="Seleccione"
                      style={{ width: "100%" }}
                      value={origenWp}
                      onChange={(e) => setOrigenWp(e.value)}
                    />
                  </div>

                  {/* Titular */}
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Nombre Titular
                    </label>
                    <InputText
                      placeholder="Nombre del titular de la tarjeta"
                      style={{ width: "100%" }}
                      value={titularWp}
                      onChange={(e) => setTitularWp(e.target.value)}
                    />
                  </div>

                  {/* Otro Titular */}
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Apellido Titular
                    </label>
                    <InputText
                      placeholder="Ej: Apellido del titular de la tarjeta"
                      style={{ width: "100%" }}
                    />
                  </div>

                  {/* Cuotas */}
                  <div style={{ width: "100%" }}>
                    <label
                      style={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                        marginBottom: 4,
                        display: "block",
                      }}
                    >
                      Cuotas
                    </label>
                    <InputText
                      placeholder="Ej: 6"
                      style={{ width: "100%" }}
                      value={cuotasWp}
                      type="number"
                      onChange={(e) => setCuotasWp(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Observaciones */}
              <div>
                <h4
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    marginBottom: "0.5rem",
                  }}
                >
                  Observaciones
                </h4>
                <hr style={{ borderColor: "#eee", marginBottom: "1rem" }} />
                <InputText
                  placeholder="Escriba aquí"
                  style={{ width: "100%" }}
                  value={obsWebpay}
                  onChange={(e) => setObsWebpay(e.target.value)}
                />
              </div>

              {/* Guardar */}
              <div>
                <Button
                  label={savingWebpay ? "Guardando..." : "Guardar"}
                  icon="pi pi-save"
                  outlined
                  style={{
                    borderRadius: "20px",
                    padding: "0.6rem 1.2rem",
                    color: "#2b94f0",
                    borderColor: "#2b94f0",
                    fontWeight: 600,
                  }}
                  disabled={errorMontoWebpay || savingWebpay}
                  onClick={handleGuardarWebpay}
                />

                {msgWebpay && (
                  <small
                    style={{ color: "#0b7e20", marginTop: 8, display: "block" }}
                  >
                    {msgWebpay}
                  </small>
                )}
                {errWebpay && (
                  <small
                    style={{ color: "#e24c4c", marginTop: 8, display: "block" }}
                  >
                    {errWebpay}
                  </small>
                )}
              </div>
            </div>
          </TabPanel>
        </TabView>
      </div>
    </div>
  );
};

export default Gestion;
